// src/hooks/useUserActions.js
import { useCallback } from "react";
import { updateUsuario, obtenerUsuarioPorId } from "../../services/api";
import { USER_FIELDS } from "../../models/userFields";
import toast from 'react-hot-toast';

export function useUserActions(usuario, setUsuario) {
  // Función para preparar los datos del usuario, muy usada en "Mis datos" y "Configuraciones"
  const prepareUserData = useCallback(() => {
    if (!usuario) {
      return {};
    }
    
    const tempData = {};
    Object.keys(USER_FIELDS).forEach((k) => {
      let val = usuario[k];
      
      // Mapeo específico para propiedades que pueden tener nombres diferentes
      if (!val && k === 'nombrePropietario') {
        val = usuario.nombre;
      }
      if (!val && k === 'correoPropietario') {
        val = usuario.correo || usuario.email;
      }
      
      // Mapear objeto ubicacion a campos planos
      if (k === 'ubicacionCiudad') {
        val = val || usuario.ubicacion?.ciudad || '';
      }
      if (k === 'ubicacionEstado') {
        val = val || usuario.ubicacion?.estado || '';
      }
      if (k === 'ubicacionPais') {
        val = val || usuario.ubicacion?.pais || '';
      }
      
      tempData[k] = Array.isArray(val) ? val : (val ?? USER_FIELDS[k].default);
    });
    return tempData;
  }, [usuario]);

  // Actualización de datos del usuario (para "Mis datos")
  const saveUserData = useCallback(
  async (updated) => {
    try {
      const userId = usuario.ownerId || usuario.id;
      const resp = await updateUsuario(userId, updated);
      
      if (resp.error) {
        toast.error("Error al actualizar el usuario: " + resp.error);
        return false;
      }
      
      // ✅ Recargar usuario completo desde backend
      const usuarioActualizado = await obtenerUsuarioPorId(userId);
      
      if (usuarioActualizado && !usuarioActualizado.error) {
        // ✅ SIMPLIFICADO: Confiar en lo que retorna el backend
        setUsuario((prev) => ({ 
          ...prev, 
          ...usuarioActualizado 
        }));
      }
      
      return true;
    } catch (err) {
      console.error("Error actualizando usuario =>", err);
      toast.error("No se pudo guardar los datos del usuario.");
      return false;
    }
  },
  [usuario, setUsuario]
);

  // Actualización para "Configuraciones"
  const saveProfileEdit = useCallback(
    async (finalData) => {
      try {
        const userId = usuario.ownerId || usuario.id;
        const resp = await updateUsuario(userId, finalData);
        
        if (resp.error) {
          toast.error("Error al actualizar el usuario: " + resp.error);
          return false;
        }
        
        // ✅ NUEVO: Sincronizar con backend
        const usuarioActualizado = await obtenerUsuarioPorId(userId);
        
        if (usuarioActualizado && !usuarioActualizado.error) {
          // ✅ CORREGIDO: Asegurar que ubicacion.configurada sea true si hay ciudad
          const datosConUbicacion = {
            ...usuarioActualizado,
            ubicacion: (usuarioActualizado.ciudad || 
                       usuarioActualizado.ubicacion?.ciudad || 
                       finalData.ubicacionCiudad || 
                       finalData.ciudad)
              ? { 
                  ...usuarioActualizado.ubicacion,
                  configurada: true,
                  ciudad: usuarioActualizado.ciudad || 
                         usuarioActualizado.ubicacion?.ciudad || 
                         finalData.ubicacionCiudad || 
                         finalData.ciudad,
                  estado: usuarioActualizado.estado || 
                         usuarioActualizado.ubicacion?.estado || 
                         finalData.ubicacionEstado || 
                         finalData.estado
                }
              : usuarioActualizado.ubicacion
          };
          setUsuario((prev) => ({ ...prev, ...datosConUbicacion }));
        } else {
          // Fallback: actualizar solo con lo que se envió
          const datosConUbicacion = {
            ...finalData,
            ubicacion: (finalData.ubicacionCiudad || finalData.ciudad)
              ? { 
                  configurada: true,
                  ciudad: finalData.ubicacionCiudad || finalData.ciudad,
                  estado: finalData.ubicacionEstado || finalData.estado
                }
              : usuario.ubicacion
          };
          setUsuario((prev) => ({ ...prev, ...datosConUbicacion }));
        }
        
        return true;
      } catch (err) {
        console.error("Error actualizando usuario =>", err);
        toast.error("No se pudo guardar los datos del usuario.");
        return false;
      }
    },
    [usuario, setUsuario]
  );

  // ✅ FIX: Cambio de avatar - AHORA PERSISTE EN BASE DE DATOS
  const changeAvatar = useCallback(
    async (newAvatar) => {
      try {
        const userId = usuario.ownerId || usuario.id;
        
        if (!userId) {
          toast.error("Usuario no identificado");
          return false;
        }
        
        // ✅ Guardar en base de datos usando la función que YA existe
        const resp = await updateUsuario(userId, { urlAvatar: newAvatar });
        
        if (resp.error) {
          toast.error("Error al guardar el avatar: " + resp.error);
          return false;
        }
        
        // ✅ Actualizar estado local
        setUsuario((prev) => ({ ...prev, urlAvatar: newAvatar }));
        
        toast.success("Avatar actualizado correctamente");
        return true;
        
      } catch (err) {
        console.error("Error guardando avatar =>", err);
        toast.error("No se pudo guardar el avatar.");
        return false;
      }
    },
    [usuario, setUsuario]
  );

  return { prepareUserData, saveUserData, saveProfileEdit, changeAvatar };
}